import React, { LegacyRef, ReactNode, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { getAllNotification } from '../../api/notifications/notification'
import { notificationResponse, notifications } from '../../utils/types'
import { formatDate } from '../../utils/utils'

import { Icon } from '../Icon'
import Loading from './Loading'

interface MenuProps {
  isOpen: boolean
  children: ReactNode
  wrapperRefNotificationMenu: LegacyRef<HTMLDivElement> | undefined
}

const NotifcationCard = ({
  label,
  description,
  createdAt,
}: {
  label: string
  description: string
  createdAt: string
}) => {
  return (
    <div className="bg-white rounded-2xl p-4 cursor-pointer">
      <div className="flex gap-3">
        <div className="flex items-center justify-center rounded-full bg-bluishGreen text-white w-6 h-6 border-4 border-[#FFFFFF33]">
          <p className="text-[8px] text-white font-normal"></p>
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm leading-6 text-text4">{label}</p>
          <p className="font-normal text-sm leading-6 text-text1">
            {description}
          </p>
        </div>
        <div className="flex gap-4 w-fit h-full items-center">
          <p className="font-medium text-xs leading-4 text-bluishGreen">
            {formatDate(createdAt)}
          </p>
          <div className="inline-block h-[6px] w-[6px] rounded-full bg-bluishGreen"></div>
          <Icon
            name="IconChevronRight"
            width={12}
            height={12}
            strokeWidth={1}
            stroke={'#02698C'}
          />
        </div>
      </div>
    </div>
  )
}

const UINotificationMenu: React.FC<MenuProps> = ({
  isOpen,
  children,
  wrapperRefNotificationMenu,
}) => {
  const [isExpand, setIsExpand] = useState<boolean>(false)
  const getNotifications = async (): Promise<notifications[]> => {
    const response = (await getAllNotification()) as notificationResponse

    if (response?.status != 'successful') {
      toast.error('Network Error.', {
        position: 'top-right',
      })
    }
    return response?.data
  }

  const notificationsData = useQuery('notification', getNotifications)
  return (
    <>
      <div ref={wrapperRefNotificationMenu} className={`relative`}>
        {children}
        {isOpen && (
          <div
            className={`absolute right-0 mt-2 w-[375px] ${isExpand ? 'h-[700px]' : 'h-[500px]'} overflow-hidden bg-gray1 rounded-2xl z-20 shadow-lg`}
          >
            <div className="gap-[10px] bg-white rounded-t-2xl flex items-center px-4 py-4">
              <p className="text-[#000000] font-bold text-lg leading-6 flex-1">
                Notifications
              </p>
              <div className="flex gap-2 items-center">
                <p className="text-text2 font-semibold text-sm leading-6 flex-1">
                  Sort by
                </p>
                <div className="rounded-[40px] bg-gray2 py-2 px-4 flex justify-center items-center gap-1 cursor-pointer">
                  <p className={'text-text1 font-medium text-xs leading-5'}>
                    Most recent
                  </p>
                  <Icon
                    name="IconChevronDown"
                    width={16}
                    height={16}
                    strokeWidth={1}
                    stroke={'#67A5B9'}
                  />
                </div>
              </div>
            </div>
            <div className="flex-col h-full w-full mb-12">
              {notificationsData.isLoading && (
                <div className="flex justify-center items-center h-screen">
                  <Loading size="medium" />
                </div>
              )}
              {!notificationsData.isLoading && (
                <div
                  style={{
                    height: 'calc(100vh - 100px)',
                    overflow: 'auto',
                    scrollbarWidth: 'none',
                  }}
                  className="flex-col p-3 flex gap-3"
                >
                  {notificationsData?.data?.map((item, index) => {
                    return (
                      <div key={index}>
                        <NotifcationCard
                          label={item.label}
                          description={item.description}
                          createdAt={item.createdAt?.substring(0, 16)}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <div
              onClick={() => setIsExpand(!isExpand)}
              className="absolute bottom-0 w-full cursor-pointer bg-white rounded-b-2xl flex justify-center items-center px-4 py-4"
            >
              <p className="text-bluishGreen font-medium text-xs leading-5">
                Expand
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default UINotificationMenu
